// ✅ All imports - useState/useEffect only ONCE at the top
import { useState, useEffect } from 'react'
import Login from './screens/Login'
import PasswordRecovery from './screens/PasswordRecovery'
import Demo from './screens/Demo'
import { useSessionTimeout } from './hooks/useSessionTimeout'
import DashboardShell from './screens/DashboardShell'
import OnboardingWizard from './components/OnboardingWizard'
import UpgradeModal from './components/UpgradeModal'
import DevPreviewBar from './components/DevPreviewBar'
import { LedgerProvider } from './lib/LedgerContext'
import { ToastProvider } from './lib/ToastContext'
import { AuthProvider, useAuth } from './context/AuthContext'
import { COMPANY_NAV, STAFF_NAV, PLATFORM_NAV } from './data/nav'
import { companyName as defaultCompany } from './data/seed'
import { profilesApi, companiesApi } from './lib/api'
import Discover from './screens/Discover'
import PublicCompanyProfile from './screens/PublicCompanyProfile'
import InvestorVault from './screens/InvestorVault'
import Landing from './screens/Landing'
import About from './screens/About'
import Contact from './screens/Contact'
import Pricing from './screens/Pricing'
import AgentDashboard from './screens/AgentDashboard'
import AgentRegistration from './screens/AgentRegistration'
import ChatScreen from './screens/ChatScreen'

// ✅ NEW
import { useKeyboardShortcut, SHORTCUTS } from './hooks/useKeyboard'
import { trackEvent, Events } from './lib/analytics'
import VerifyEmail from './screens/VerifyEmail'

// ✅ Theme imports
import { ThemeProvider } from './lib/theme'
import './styles/globals.css'

// ✅ Helper to determine if we should show DevPreviewBar
const shouldShowDevBar = () => {
  if (import.meta.env.PROD) return false
  return import.meta.env.DEV || import.meta.env.VITE_DEPLOY_ENV === 'staging'
}

const isProfileComplete = (profile) => {
  if (!profile) return false
  const { kyc, business } = profile
  return Boolean(
    kyc?.fullName && kyc?.idType && kyc?.phone && kyc?.address &&
      business?.businessName && business?.country && business?.corporateAddress && business?.industry,
  )
}

const DEFAULT_PREFS = { country: 'Nigeria', currency: 'NGN', dateFormat: 'DD/MM/YYYY', timezone: 'Africa/Lagos (WAT)' }

function AppInner() {
  const { user, profile, company: authCompany, role, onboardingComplete, refreshProfile, signOut } = useAuth()
  const [profileNav, setProfileNav] = useState(null)
  const [view, setView] = useState('login')
  const [company, setCompany] = useState(defaultCompany)
  const [profileData, setProfileData] = useState(null)
  const [isPremium, setIsPremium] = useState(false)
  const [showWizard, setShowWizard] = useState(false)
  const [showUpgrade, setShowUpgrade] = useState(false)
  const [prefs, setPrefs] = useState(DEFAULT_PREFS)
  const [agentRequests, setAgentRequests] = useState([])
  const [publicSlug, setPublicSlug] = useState(null)
  const [publicPage, setPublicPage] = useState(null)
  const [demoMode, setDemoMode] = useState(false)
  const [investorToken, setInvestorToken] = useState(null)
  const [devView, setDevView] = useState(null)
  const [sessionWarning, setSessionWarning] = useState(false)

  const { reset: resetSession } = useSessionTimeout({
    onTimeout: () => { signOut?.(); setDevView(null); setPublicPage('landing') },
    onWarning: () => setSessionWarning(true),
    onActivity: () => setSessionWarning(false),
  })

  // ✅ Keyboard shortcuts
  useKeyboardShortcut(SHORTCUTS.SEARCH, () => {
    const searchInput = document.querySelector('input[placeholder*="Search"]')
    if (searchInput) {
      searchInput.focus()
      searchInput.select()
    }
  })

  useKeyboardShortcut(SHORTCUTS.DASHBOARD, () => {
    handleSwitchView('dashboard')
    trackEvent(Events.PAGE_VIEW, { path: 'dashboard', shortcut: true })
  })

  useKeyboardShortcut(SHORTCUTS.LEGAL, () => {
    handleSwitchView('legal')
    trackEvent(Events.PAGE_VIEW, { path: 'legal', shortcut: true })
  })

  useKeyboardShortcut(SHORTCUTS.MESSAGES, () => {
    handleSwitchView('messages')
    trackEvent(Events.PAGE_VIEW, { path: 'messages', shortcut: true })
  })

  useKeyboardShortcut(SHORTCUTS.SETTINGS, () => {
    handleSwitchView('preferences')
    trackEvent(Events.PAGE_VIEW, { path: 'settings', shortcut: true })
  })

  useKeyboardShortcut(SHORTCUTS.NEW, () => {
    if (effectiveView === 'admin') {
      const addButton = document.querySelector('[data-add-transaction]')
      if (addButton) addButton.click()
    }
  })

  // ✅ Handle URL paths - only for special cases
  useEffect(() => {
    const path = window.location.pathname

    // ✅ Handle email verification route
    if (path === '/auth/verify') {
      setPublicPage(null)
      setDevView('verify-email')
      setView('verify-email')
      return
    }

    // ✅ Handle password recovery route
    if (path === '/auth/recovery') {
      setDemoMode(false)
      setPublicPage(null)
      setDevView('password-recovery')
      setView('password-recovery')
      return
    }

    // ✅ Handle demo route
    if (path === '/demo') {
      setDemoMode(false)
      setPublicPage('demo')
      setDevView(null)
      setView('demo')
      return
    }

    const vaultMatch = path.match(/^\/investor\/vault\/([\w-]+)/i)
    if (vaultMatch) {
      setInvestorToken(vaultMatch[1])
      setView('investor')
      return
    }

    const match = path.match(/^\/company\/([a-z0-9-]+)/i)
    if (match) setPublicSlug(match[1])
  }, [])

  // ✅ Handle user authentication state
  useEffect(() => {
    if (investorToken || demoMode) return
    if (window.location.pathname === '/auth/recovery') return
    if (window.location.pathname === '/auth/verify') return
    if (window.location.pathname === '/demo') return
    if (!user) { 
      setView('login'); 
      setPublicPage('landing'); 
      return 
    }

    if (onboardingComplete) {
      const userRole = role || 'founder'
      if (userRole === 'admin') {
        setView('platform')
      } else if (userRole === 'staff') {
        setView('staff')
      } else if (userRole === 'agent') {
        setView('agent-projects')
      } else {
        setView('admin') // founder
      }
      setShowWizard(false)
    } else if (profile && !onboardingComplete) {
      setView('admin')
      setShowWizard(true)
    } else if (user && !profile) {
      setView('admin')
      setShowWizard(true)
    }
  }, [user, profile, role, onboardingComplete, investorToken, demoMode])

  // ✅ Helper function to handle onboarding close for ALL roles
  const handleOnboardingClose = () => {
    setShowWizard(false)
    
    if (!user) {
      setView('login')
      return
    }
    
    const userRole = role || profile?.role || 'founder'
    
    switch (userRole) {
      case 'admin':
        setView('platform')
        setDevView('platform')
        break
      case 'staff':
        setView('staff')
        setDevView('staff')
        break
      case 'agent':
        setView('agent-projects')
        setDevView('agent-projects')
        break
      case 'founder':
      default:
        setView('admin')
        setDevView('admin')
        break
    }
  }

  const profileComplete = isProfileComplete(profileData)
  const country = prefs.country || authCompany?.country || 'Nigeria'

  const handleOnboarded = ({ company: companyName, founderEmail, country: signupCountry, currency: signupCurrency, role: signupRole, companyId, onboardingComplete: obComplete }) => {
    setCompany(companyName || (founderEmail ? founderEmail.split('@')[0] + ' Business' : 'My Business'))
    if (signupCountry) {
      setPrefs((p) => ({ ...p, country: signupCountry, currency: signupCurrency || p.currency }))
    }

    if (!obComplete) {
      setView('admin')
      setShowWizard(true)
    } else {
      setView(signupRole === 'admin' ? 'platform' : 'admin')
    }
  }

  const handleSaveOnboarding = async (data) => {
    if (!user?.id) return
    try {
      await profilesApi.update(user.id, {
        full_name: data.fullName,
        id_type: data.idType,
        phone: data.phone,
        home_address: data.address,
        onboarding_complete: true,
      })
      if (authCompany?.id) {
        await companiesApi.update(authCompany.id, {
          name: data.businessName,
          country: data.country,
          corporate_address: data.corporateAddress,
          industry: data.industry,
        })
      }
      setProfileData({
        kyc: { fullName: data.fullName, idType: data.idType, phone: data.phone, address: data.address },
        business: { businessName: data.businessName, country: data.country, corporateAddress: data.corporateAddress, industry: data.industry },
        directors: [],
      })
      setShowWizard(false)
      await refreshProfile?.()
      
      const userRole = role || 'founder'
      if (userRole === 'admin') {
        setView('platform')
        setDevView('platform')
      } else if (userRole === 'staff') {
        setView('staff')
        setDevView('staff')
      } else if (userRole === 'agent') {
        setView('agent-projects')
        setDevView('agent-projects')
      } else {
        setView('admin')
        setDevView('admin')
      }
    } catch (err) {
      console.error('Failed to save onboarding:', err)
    }
  }

  const handleSaveProfile = (data) => setProfileData(data)
  const handleUpgrade = () => { setIsPremium(true); setShowUpgrade(false) }
  const handleUpgradeRequest = () => setShowUpgrade(true)
  const handleSavePrefs = (newPrefs) => setPrefs(newPrefs)
  const handleAgentRequest = (payload) => {
    setAgentRequests((prev) => [...prev, { ...payload, id: `AG-${String(prev.length + 22).padStart(3, '0')}`, date: new Date().toISOString().slice(0, 10), status: 'Open' }])
  }

  const exitInvestor = () => {
    setInvestorToken(null)
    setView('login')
    window.history.pushState({}, '', '/')
  }

  if (view === 'investor' && investorToken) {
    return (
      <ToastProvider>
        <InvestorVault token={investorToken} onExit={exitInvestor} />
      </ToastProvider>
    )
  }

  const authUser = { name: profile?.full_name, email: user?.email }

  // ✅ Handle navigation - NO URL changes for public pages
  const handleSwitchView = (target) => {
    // ✅ Special routes
    if (target === 'agent-registration') {
      setDevView('agent-registration');
      setPublicPage(null);
      return
    }
    // ✅ Public pages
    if (target === 'landing') { 
      setPublicPage('landing'); 
      setDevView(null); 
      return 
    }
    if (target === 'about') { 
      setPublicPage('about'); 
      setDevView(null); 
      return 
    }
    if (target === 'contact') { 
      setPublicPage('contact'); 
      setDevView(null); 
      return 
    }
    if (target === 'pricing') { 
      setPublicPage('pricing'); 
      setDevView(null); 
      return 
    }
    if (target === 'demo') { 
      setPublicPage('demo'); 
      setDevView(null); 
      setDemoMode(false);
      return 
    }
    if (target === 'login') { 
      signOut(); 
      setDevView(null); 
      setPublicPage(null); 
      return 
    }
    // ✅ Dashboard views
    if (target === 'agent-projects' || target === 'agent-chat' || target === 'agent-profile' || target === 'agent-upload') {
      setDevView(target); 
      setPublicPage(null); 
      return
    }
    if (target === 'messages') { 
      setDevView('messages'); 
      setPublicPage(null); 
      return 
    }
    // ✅ Default: switch to dashboard view
    setDemoMode(false)
    setDevView(target)
    setPublicPage(null)
  }

  const effectiveView = devView || view

  const handleLogout = () => { signOut(); setDevView(null); setPublicPage('landing') }

  // ✅ FIXED: Handle profile click - navigates to profile based on role
  // ✅ FIXED: handleProfileClick - ONLY changes profileNav
const handleProfileClick = () => {
  const currentRole = role || profile?.role || 'founder'
  
  console.log('🔵 App - PROFILE CLICK - Role:', currentRole)
  
  // ✅ ONLY set profileNav - NEVER change devView
  switch (currentRole) {
    case 'admin':
      setProfileNav('platform-settings')
      break
      
    case 'staff':
      setProfileNav('staff-profile')
      break
      
    case 'agent':
      setProfileNav('agent-profile')
      break
      
    case 'founder':
    default:
      setProfileNav('profile')
      break
  }
}

  return (
    <ThemeProvider defaultTheme="light">
      <div className="min-h-full flex flex-col">
        {shouldShowDevBar() && (
          <DevPreviewBar
            currentView={publicPage || (effectiveView === 'login' ? 'login' : devView || view)}
            onSwitchView={handleSwitchView}
          />
        )}

        {publicPage ? (
          <>
            {publicPage === 'landing' && <Landing onNavigate={handleSwitchView} onGetStarted={() => { setPublicPage(null); setDevView('login') }} />}
            {publicPage === 'about' && <About onNavigate={handleSwitchView} onGetStarted={() => { setPublicPage(null); setDevView('login') }} />}
            {publicPage === 'contact' && <Contact onNavigate={handleSwitchView} onGetStarted={() => { setPublicPage(null); setDevView('login') }} />}
            {publicPage === 'pricing' && <Pricing onNavigate={handleSwitchView} onGetStarted={() => { setPublicPage(null); setDevView('login') }} />}
            {publicPage === 'demo' && <Demo onNavigate={handleSwitchView} onGetStarted={() => { setPublicPage(null); setDevView('login') }} />}
          </>
        ) : (
          <LedgerProvider demoMode={demoMode}>
            {effectiveView === 'login' && <Login onOnboarded={handleOnboarded} />}

            {effectiveView === 'verify-email' && (
              <VerifyEmail 
                onVerified={() => {
                  setDevView('admin');
                  setView('admin');
                }} 
              />
            )}

            {effectiveView === 'password-recovery' && (
              <PasswordRecovery 
                onBackToSignIn={() => { 
                  setDevView('login'); 
                  setView('login'); 
                }} 
              />
            )}

            {effectiveView === 'agent-registration' && (
              <div className="flex-1 overflow-y-auto bg-neutral-50">
                <AgentRegistration
                  onComplete={() => handleSwitchView('agent-projects')}
                  onCancel={() => handleSwitchView('landing')}
                />
              </div>
            )}

            {effectiveView === 'admin' && (
              <DashboardShell
                nav={COMPANY_NAV}
                company={authCompany?.name || company}
                companyId={authCompany?.id}
                user={authUser}
                authProfile={profile}
                role="founder"
                profile={profileData}
                profileComplete={profileComplete}
                isPremium={isPremium}
                onSaveProfile={handleSaveProfile}
                onUpgrade={handleUpgradeRequest}
                country={country}
                prefs={prefs}
                onSavePrefs={handleSavePrefs}
                onAgentRequest={handleAgentRequest}
                demoMode={demoMode}
                onLogout={handleLogout}
                onProfileClick={handleProfileClick}
                initialTab={profileNav}
              />
            )}

            {effectiveView === 'staff' && (
              <DashboardShell
                nav={STAFF_NAV}
                company={null}
                user={authUser}
                authProfile={profile}
                role="staff"
                prefs={prefs}
                onLogout={handleLogout}
                onProfileClick={handleProfileClick}
              />
            )}

            {effectiveView === 'platform' && (
              <DashboardShell
                nav={PLATFORM_NAV}
                company={authCompany?.name || company}
                companyId={authCompany?.id}
                user={authUser}
                authProfile={profile}
                role="admin"
                country={country}
                prefs={prefs}
                onLogout={handleLogout}
                onProfileClick={handleProfileClick}
              />
            )}

            {effectiveView === 'discover' && (
              <Discover onAccessRequest={handleAgentRequest} />
            )}

            {(effectiveView === 'agent-projects' || effectiveView === 'agent-chat' || effectiveView === 'agent-profile' || effectiveView === 'agent-upload') && (
              <div className="flex-1 overflow-y-auto bg-neutral-50">
                <AgentDashboard activeTab={effectiveView} onNavigate={handleSwitchView} />
              </div>
            )}

            {effectiveView === 'messages' && (
              <div className="flex-1 overflow-y-auto bg-neutral-50">
                <ChatScreen onNavigate={handleSwitchView} />
              </div>
            )}

            {publicSlug && (
              <PublicCompanyProfile slug={publicSlug} />
            )}
          </LedgerProvider>
        )}

        <OnboardingWizard
          open={showWizard}
          onClose={handleOnboardingClose}
          onSave={handleSaveOnboarding}
          onSkip={handleOnboardingClose}
        />

        <UpgradeModal
          open={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          onUpgrade={handleUpgrade}
        />

        {sessionWarning && (
          <div className="fixed bottom-4 right-4 z-50 bg-amber-50 border border-amber-300 rounded-lg shadow-lg p-4 max-w-xs">
            <p className="text-xs font-semibold text-amber-800 mb-1">Session expiring soon</p>
            <p className="text-xs text-amber-700 mb-3">You will be logged out due to inactivity. Click to stay signed in.</p>
            <button onClick={resetSession} className="btn-primary text-xs w-full">Stay signed in</button>
          </div>
        )}
      </div>
    </ThemeProvider>
  )
}

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </ToastProvider>
  )
}